/**
 * Accept Invitation Page (Triveny)
 *
 * Lands here from the invitation email link:
 *   https://app.triveny.ai/accept-invitation?token=pinv_...
 *
 * Reads the token from the URL, prompts the invitee for a password
 * (with confirm), and POSTs to /api/platform/auth/accept-invite. On
 * success the backend creates a platform_shared.tenant_users row and
 * returns an access_token + refresh_token + user/tenant — we stash
 * those in localStorage and route to the Triveny home surface.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import { AlertCircle, Lock, CheckCircle2, Mail, RefreshCw } from 'lucide-react';

interface AcceptResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; first_name?: string; last_name?: string; role?: string };
  tenant: { id: string; slug: string; name: string };
}

export default function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Self-serve "request a fresh invitation" flow — shown when the URL
  // token doesn't match any row (e.g. user clicked a stale email after
  // the admin cancelled or replaced the invitation).
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendDone, setResendDone] = useState<string | null>(null);
  // True when the BE returned a 400 indicating the token is unknown or
  // already used — that's when we offer the self-serve resend flow.
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing invitation token. Please use the link from your email.');
  }, [token]);

  const validate = (): string | null => {
    if (!token) return 'Missing invitation token';
    if (!password) return 'Please choose a password';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password !== confirm) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        `${config.weaveinApi.baseUrl}/api/platform/auth/accept-invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invitation_token: token, password }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface the backend's actual reason. The three meaningful 400s
        // from /api/platform/auth/accept-invite are:
        //   "Invalid invitation token" — token doesn't match any row (often
        //       a stale link from an earlier email after the admin clicked
        //       Resend, which rotates the token)
        //   "Invitation already used" — accepted_at is set on the row
        //   "Invitation has expired" — expires_at has passed
        // The previous generic fallback "may be expired or already used"
        // hid the stale-link case behind misleading copy.
        const detail = data?.detail || '';
        let friendly = detail || 'We couldn\'t accept this invitation.';
        let canSelfResend = false;
        if (/invalid.*token/i.test(detail)) {
          friendly =
            'This invitation link is no longer valid. ' +
            'It may have been replaced by a newer invitation or already used.';
          canSelfResend = true;
        } else if (/already used/i.test(detail)) {
          friendly =
            'This invitation has already been used. If that wasn\'t you, contact your admin — otherwise sign in with the password you set.';
        } else if (/expired/i.test(detail)) {
          friendly =
            'This invitation has expired. Triveny invitations are valid for 7 days; request a fresh one below or ask your admin to resend it.';
          canSelfResend = true;
        }
        setError(friendly);
        setTokenInvalid(canSelfResend);
        setSubmitting(false);
        return;
      }
      const out = data as AcceptResponse;
      // Land in the same logged-in shape as the normal login flow so
      // App.tsx + AuthContext pick up the platform session cleanly.
      localStorage.setItem('auth_token', out.access_token);
      localStorage.setItem('refresh_token', out.refresh_token);
      localStorage.setItem('session_start', Date.now().toString());
      try {
        localStorage.setItem('user', JSON.stringify(out.user));
        localStorage.setItem('tenant', JSON.stringify(out.tenant));
      } catch {
        /* ignore quota */
      }
      localStorage.removeItem('activeTab');
      setDone(true);
      // brief success state, then route into the app
      setTimeout(() => navigate('/'), 800);
    } catch (err: any) {
      setError(err?.message || 'Network error');
      setSubmitting(false);
    }
  };

  // Self-serve: ask the BE to resend the invitation email to the address
  // the invitee types in. The BE looks up an unaccepted, non-held row by
  // email and re-sends the EXISTING invitation_token + extended expiry.
  // Returns 200 regardless of whether a row exists (enumeration-safe);
  // the response message tells the user to check inbox + spam.
  const handleResendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = resendEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setResendDone('Please enter a valid email address.');
      return;
    }
    setResendSubmitting(true);
    setResendDone(null);
    try {
      const res = await fetch(
        `${config.weaveinApi.baseUrl}/api/platform/auth/request-invite-resend`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed }),
        },
      );
      const data = await res.json().catch(() => ({}));
      setResendDone(
        data?.message ||
          'If your email has a pending invitation, we just sent a fresh link. Check your inbox and spam folder.',
      );
    } catch (err: any) {
      // Surface the network error without leaking whether a row exists.
      setResendDone(
        'Couldn\'t reach the server. Please try again in a moment.',
      );
    } finally {
      setResendSubmitting(false);
    }
  };

  return (
    <div
      className="triveny-platform"
      style={{
        minHeight: '100vh',
        background: tvTokens.paper,
        fontFamily: tvTokens.fontSans,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          border: `1px solid ${tvTokens.border}`,
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 1px 3px rgba(20,23,28,0.04)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src="/triveny/logo.svg"
            alt="Triveny"
            style={{ height: 28, width: 'auto', display: 'inline-block' }}
          />
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2
              size={48}
              style={{ color: '#009a83', margin: '0 auto 12px', display: 'block' }}
            />
            <h1 style={{ fontSize: 20, fontWeight: 600, color: tvTokens.ink, margin: 0 }}>
              You're in
            </h1>
            <p style={{ color: tvTokens.ink700, fontSize: 14, marginTop: 8 }}>
              Taking you to your workspace…
            </p>
          </div>
        ) : (
          <>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: tvTokens.ink,
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              Accept your invitation
            </h1>
            <p style={{ color: tvTokens.ink700, fontSize: 14, marginTop: 6 }}>
              Set a password to finish creating your Triveny account.
            </p>

            {error && (
              <div
                style={{
                  marginTop: 16,
                  padding: '10px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  color: '#991b1b',
                  fontSize: 13,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{error}</span>
              </div>
            )}

            {/* Self-serve fresh-link request — shown only when the URL
                token is unknown to the BE or expired. The endpoint is
                enumeration-safe (always returns the same 200 response
                regardless of whether the email matches an existing
                invitation), so this form can be public on the page. */}
            {tokenInvalid && !showResendForm && (
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowResendForm(true)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    color: tvTokens.ink,
                    border: `1px solid ${tvTokens.border}`,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <RefreshCw size={14} /> Request a new invitation
                </button>
              </div>
            )}

            {tokenInvalid && showResendForm && (
              <form
                onSubmit={handleResendRequest}
                style={{
                  marginTop: 14,
                  padding: 14,
                  background: '#fafaf7',
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: tvTokens.ink,
                    marginBottom: 6,
                  }}
                >
                  We&apos;ll send a fresh invitation link to:
                </div>
                <div style={{ position: 'relative' }}>
                  <Mail
                    size={16}
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: tvTokens.ink500,
                    }}
                  />
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoComplete="email"
                    required
                    disabled={resendSubmitting}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 36px',
                      border: `1px solid ${tvTokens.border}`,
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: tvTokens.fontSans,
                      background: '#fff',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 10,
                    justifyContent: 'flex-end',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowResendForm(false);
                      setResendDone(null);
                    }}
                    disabled={resendSubmitting}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      color: tvTokens.ink,
                      border: `1px solid ${tvTokens.border}`,
                      borderRadius: 8,
                      fontSize: 12,
                      cursor: resendSubmitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resendSubmitting || !resendEmail.trim()}
                    style={{
                      padding: '8px 14px',
                      background: tvTokens.ink,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: resendSubmitting || !resendEmail.trim() ? 'not-allowed' : 'pointer',
                      opacity: resendSubmitting || !resendEmail.trim() ? 0.6 : 1,
                    }}
                  >
                    {resendSubmitting ? 'Sending…' : 'Send fresh link'}
                  </button>
                </div>
                {resendDone && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: tvTokens.ink700,
                      padding: '8px 10px',
                      background: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: 6,
                    }}
                  >
                    {resendDone}
                  </div>
                )}
              </form>
            )}

            <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
              <label style={{ display: 'block', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: tvTokens.ink,
                    marginBottom: 6,
                  }}
                >
                  New password
                </span>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={16}
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: tvTokens.ink500,
                    }}
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={!token || submitting}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 36px',
                      border: `1px solid ${tvTokens.border}`,
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: tvTokens.fontSans,
                      background: '#fff',
                    }}
                  />
                </div>
              </label>
              <label style={{ display: 'block', marginBottom: 18 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: tvTokens.ink,
                    marginBottom: 6,
                  }}
                >
                  Confirm password
                </span>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={16}
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: tvTokens.ink500,
                    }}
                  />
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={!token || submitting}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 36px',
                      border: `1px solid ${tvTokens.border}`,
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: tvTokens.fontSans,
                      background: '#fff',
                    }}
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={!token || submitting}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  background: tvTokens.ink,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: !token || submitting ? 'not-allowed' : 'pointer',
                  opacity: !token || submitting ? 0.6 : 1,
                  fontFamily: tvTokens.fontSans,
                }}
              >
                {submitting ? 'Creating your account…' : 'Accept invitation'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
